import {
  Component,
  computed,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, FormGroupDirective, Validators } from "@angular/forms";
import { MatSidenav } from "@angular/material/sidenav";
import { MatStep, MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom, Subscription } from "rxjs";
import { AppError } from "src/app/common/validators/app-error";
import {
  FormsIds,
  ServiceRequestStepType,
} from "src/app/components/service-request/models/ServiceRequestStepType";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import CustomerSettings from "src/app/models/CustomerSettings";
import { ScheduleDueType, StatusTypes, ViewType } from "src/app/models/Enums";
import { EquipmentId } from "src/app/models/EquipmentId";
import { FileMetaData, FileToUpload } from "src/app/models/FileToUpload";
import {
  ParcelDimensionsOptionsByUnitOfMeasure,
  ParcelOption,
} from "src/app/models/ParcelDimensionsOption";
import { RaOptions } from "src/app/models/RaOptions";
import { EventDocFile, RaRequestResponse, ShipmentLabel } from "src/app/models/RaRequestResponse";
import { ServiceRequestEquipmentItem } from "src/app/models/ServiceRequestEquipmentItem";
import { ShipmentPickupDetails } from "src/app/models/ShipmentPickupDetails";
import { BrandingService } from "src/app/services/branding.service";
import { CompanyService } from "src/app/services/company.service";
import { CustomerSettingsDataService } from "src/app/services/customer-settings-data.service";
import { EventService } from "src/app/services/event.service";
import { FileService } from "src/app/services/file.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalService } from "src/app/services/modal.service";
import { StorageService } from "src/app/services/storage.service";
import { ToolService } from "src/app/services/tool.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";
import { DateUtils } from "src/app/utils/date.utils";
import { isAnyControlInvalid } from "src/app/utils/forms.utils";
import { cleanPhoneNumber } from "src/app/utils/phone.utils";

import { Address } from "./../../models/Address";
import { FormKeys, ServiceRequest, Shipment } from "./../../models/ServiceRequest";
import { UserVendor } from "./../../models/UserVendor";
import { AuthService } from "./../../services/auth.service";
import { RaBasketService } from "./../../services/ra-basket.service";

@Component({
  selector: "app-service-request",
  templateUrl: "./service-request.component.html",
  styleUrls: ["./service-request.component.scss"],
  standalone: false,
})
export class ServiceRequestComponent implements OnInit, OnDestroy {
  @ViewChild("step1form") step1Form: FormGroupDirective;
  @ViewChild("step2form") step2Form: FormGroupDirective;
  @ViewChild("sideServiceRequestStepper") sideServiceRequestStepper: MatSidenav;
  @ViewChild("stepper") stepper: MatStepper;
  @ViewChild("reviewStep") reviewStep: MatStep;
  private readonly _subscriptions$: Subscription[] = [];
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private raUploadedFile: File;
  isStartRequestButtonPressed: boolean;

  isShippingStepContinuePressed: boolean;
  isDetailStepContinuePressed: boolean;

  faUpload = faUpload;
  faDownload = faDownload;
  isRaResultReceived: boolean;
  currentShipFrom: Address;
  shipFromList: Address[];
  vendorList = signal<UserVendor[]>([]);
  raRequestResult: RaRequestResponse;
  raDocument: EventDocFile;
  isStepperVisible = false;
  shippingParcelsFormArray: FormArray<FormGroup>;
  showNoParcelsError = false;
  poRequired = false;

  options: RaOptions;
  addressInput: string;

  serviceRequestStepType = ServiceRequestStepType;
  formsIds = FormsIds;

  customerSettings?: CustomerSettings;

  shipmentCreatedResult?: ShipmentLabel;

  ra = signal<ServiceRequest>(new ServiceRequest());

  selectedVendorName = computed<string>(() => {
    const vendor = this.vendorList().find((x) => x.vendorName === this.ra().vendor);
    return vendor?.vendorDescription ?? vendor?.vendorName;
  });

  constructor(
    public toolService: ToolService,
    public companyService: CompanyService,
    public basket: RaBasketService,
    private eventService: EventService,
    private globalOverlayService: GlobalOverlayService,
    private storage: StorageService,
    private authService: AuthService,
    private router: Router,
    private zone: NgZone,
    private el: ElementRef,
    private fileService: FileService,
    public brandingService: BrandingService,
    private customerSettingsDataService: CustomerSettingsDataService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private toolFilterService: ToolFilterService,
  ) {}

  ngOnInit(): void {
    this.ra.update((ra) => ({
      ...ra,
      fromContact: `${this.authService.user.firstName} ${this.authService.user.lastName}`,
      customerEmail: this.authService.user.email,
      phoneNumber: this.authService.user.phoneNumber,
    }));
    const compSub = this.onCompanyChange();
    const basketSub = this.onBasketChange();
    this._subscriptions$.push(compSub);
    this._subscriptions$.push(basketSub);
    this.performCompanyChangeAsync();
  }

  private async performCompanyChangeAsync() {
    this.ensureCompanySelected();

    this.globalOverlayService.show();
    const promises = [this.getCustomerSettingsAsync(), this.getOptionsAsync()];
    try {
      await Promise.all(promises);
      this.getTools();
    } finally {
      this.globalOverlayService.close();
    }
  }

  private onBasketChange(): Subscription {
    return this.basket.toolsChange$.subscribe(() => {
      this.getTools();
    });
  }

  private ensureCompanySelected(): void {
    // if company is not selected, inform the user!
    // TODO: implement proper global handler in case the company is not selected.
    if (!this.companyService.selectedCompany) {
      throw new AppError(
        `${ServiceRequestComponent.name}: Please select company before proceeding!`,
      );
    }
  }

  // TODO: refactor to this to have a correct naming across the app. Instead of `tool` should be `equipment`.
  getTools() {
    this.basket.tools = this.basket.getAll();
  }

  async getOptionsAsync() {
    const response = await lastValueFrom(
      this.eventService.getRaOptions(this.companyService.selectedCompany.name),
    );
    this.vendorList.set(response.vendors);
    this.ra.update((ra) => ({
      ...ra,
      vendor: this.vendorList().find((v) => v.isDefault)?.vendorName,
    }));
    this.options = response;
  }

  async getCustomerSettingsAsync(): Promise<void> {
    this.customerSettings = await this.customerSettingsDataService.getCustomerSettingsAsync(
      this.companyService.selectedCompany?.name,
    );
    if (this.customerSettings) {
      const shipmentControl = new FormControl<boolean>(false);
      this.step1Form.form.addControl(FormKeys.isShipmentRequested, shipmentControl);
      // TODO: Move subscription to separate function and call from ngOnInit
      shipmentControl.valueChanges.subscribe((value) => {
        this.ra.update((ra) => ({
          ...ra,
          isShipmentRequested: value,
        }));
        this.resetStepperControlState(value);
      });
    }
  }

  resetStepperControlState(isShipmentRequested: boolean) {
    // Due to an ExpressionChangedAfterItHasBeenCheckedError if switching shipmentRequested flag off and on while already on step 3
    if (isShipmentRequested) {
      this.isDetailStepContinuePressed = false;
      this.isShippingStepContinuePressed = false;
    }
  }

  /**
   * Set PO required flag based on selected max price and re set the equipment
   * to trigger change detection on child components accepting equipment as Input
   */
  onServiceFormChanged() {
    this.setPoRequired();
    this.basket.tools = [...this.basket.tools];
  }

  onCompanyChange(): Subscription {
    return this.companyService.companyChange.subscribe(() => this.performCompanyChangeAsync());
  }

  setPoRequired() {
    if (!this.options) {
      return false;
    }

    this.poRequired = this.basket.tools?.some((tool) =>
      this.options.approvedMaxPriceOptions.some(
        (x) => x.value == tool.maxPrice && x.customBoolean1 == true,
      ),
    );
  }

  disableContinueBtn(): boolean {
    return (
      (this.isShippingStepContinuePressed && this.step1Form.invalid) ||
      (this.isDetailStepContinuePressed && this.step2Form.invalid)
    );
  }

  removeAll() {
    this.basket.cleanBasket();
  }

  attachDocument(file: FileToUpload) {
    // null is emitted, in case `Cancel` button is pressed in FileDialog
    if (!file) {
      return;
    }

    const fileMetadata = new FileMetaData();
    fileMetadata.fileName = file.fileName;
    fileMetadata.fileSize = file.fileSize;
    fileMetadata.fileType = file.fileType;
    fileMetadata.lastModifiedTime = file.lastModifiedTime;
    fileMetadata.lastModifiedDate = file.lastModifiedDate;

    this.ra.update((ra) => ({
      ...ra,
      document: fileMetadata,
    }));
    this.raUploadedFile = file.fileUpload;
  }

  private toEquipmentRaRequestArray(): ServiceRequestEquipmentItem[] {
    // TODO: refactor this to use directly ServiceRequestEquipmentItem instead of Tool throughout the app.
    // `Tool` contains why more properties than ServiceRequestEquipmentItem.
    // This will avoid the mapping here and possible issues with properties missing in the mapping
    // (as ServiceRequestEquipmentItem has less properties than Tool)
    // Also rename basket.tools to basket.equipments
    // and similarly in other places across the app
    return this.basket.tools.map(
      (tool) =>
        ({
          // Map properties with same names
          ...tool,
          // Explicitly map differing property names
          equipmentId: {
            companyId: tool.companyId,
            serialNumber: tool.serialNumber,
          } as EquipmentId,
          companyId: tool.companyId,
          serialNumber: tool.serialNumber,
        }) as ServiceRequestEquipmentItem,
    );
  }

  async submitAsync() {
    this.ra.update((ra) => ({
      ...ra,
      equipments: this.toEquipmentRaRequestArray(),
      shipTo: this.currentShipFrom?.name,
      companyId: this.companyService.selectedCompany.name,
    }));

    if (this.ra().isShipmentRequested) {
      this.ra.update((ra) => {
        const shipment = this.getShipmentFormValues();
        return {
          ...ra,
          shipment,
        };
      });
    }

    this.combineCommentsForEquipments();
    this.combineRemarks();

    this.globalOverlayService.show();
    try {
      const ra = this.ra();
      // Adjust phone number before sending, as we have limit of 15 chars on backend, but on client we want to show "+"
      const raRequest = { ...ra, phoneNumber: cleanPhoneNumber(ra.phoneNumber) };
      const response = await lastValueFrom(
        this.eventService.submitServiceRequest(raRequest, this.raUploadedFile),
      );
      this.isRaResultReceived = true;
      this.raDocument = response.raDocument;
      this.shipmentCreatedResult = response.shipmentCreationResult;

      this.basket.cleanBasket();

      this.stepper.next();

      if (this.ra().isShipmentRequested && !this.shipmentCreatedResult.isShipmentCreated) {
        this.showShipmentCreationFailedNotificationAsync();
      }
    } finally {
      this.globalOverlayService.close();
    }
  }

  async showShipmentCreationFailedNotificationAsync() {
    this.modalService.openConfirmationModal({
      title: await lastValueFrom(
        this.translateService.get(
          LocalizationConstants.SERVICE_REQUEST_SHIPMENT_CREATION_FAILED_TITLE,
        ),
      ),
      content: [
        await lastValueFrom(
          this.translateService.get(
            LocalizationConstants.SERVICE_REQUEST_SHIPMENT_CREATION_FAILED_CONTACT_SUPPORT,
          ),
        ),
      ],
      isOnlyNotificationModal: true,
    });
  }

  combineCommentsForEquipments() {
    this.ra().equipments.forEach((equipment) => {
      const maxPrice = `Approved max. Service Price: ${equipment.maxPrice}\n`;
      const requestedService =
        equipment.requestedService == "other"
          ? ""
          : `Requested Service: ${equipment.requestedService}\n`;
      equipment.customerCommentsToShow = equipment.customerComments;
      equipment.customerComments = `${maxPrice}${requestedService}${
        !equipment.customerComments ? "" : equipment.customerComments
      }`;
    });
  }

  combineRemarks() {
    this.ra.update((ra) => ({
      ...ra,
      commentsToShow: this.ra().comments,
    }));

    if (this.isOtherShipFrom()) {
      const customAddress = `\n\nAddress:\n${this.currentShipFrom.displayName}\n`;

      this.ra.update((ra) => ({
        ...ra,
        comments: `${!this.ra().comments ? "\n" : this.ra().comments}${customAddress}`,
      }));
    }
  }

  private isOtherShipFrom(): boolean {
    return this.ra()?.shipTo === "other";
  }

  onToolsOutForServiceClick() {
    const filter = this.toolFilterService.create();
    filter.statusFilters = [StatusTypes.Requested];
    this.storage.setFilterState(filter, ViewType.ToolsOutForService);
    this.zone.run(() => this.router.navigate([RouteNameConstants.TOOLS_OUT_FOR_SERVICE]));
  }

  onToolsOnsiteClick() {
    const filter = this.toolFilterService.create();
    filter.serviceDueFilters = [ScheduleDueType.CalibrationDue, ScheduleDueType.ServiceDue];
    this.storage.setFilterState(filter, ViewType.ToolsOnsite);
    this.zone.run(() => this.router.navigate([RouteNameConstants.TOOLS_ONSITE]));
  }

  onStartRequestClick() {
    this.isStartRequestButtonPressed = true;
    if (isAnyControlInvalid("service-request-services-form", this.el.nativeElement)) {
      return;
    }

    this.isStepperVisible = true;
    this.sideServiceRequestStepper.open();

    // add maxLength validator for controls in Details step
    this.setDetailsStepValidators();
  }

  submitRequest() {
    this.reviewStep.completed = true;
    this.submitAsync();
  }

  closeStepper() {
    this.sideServiceRequestStepper.close();
  }

  continueStepper(type: ServiceRequestStepType) {
    if (type === ServiceRequestStepType.Review) {
      this.submitRequest();
      return;
    }

    if (type === ServiceRequestStepType.Confirmation) {
      this.closeAndResetStepper();
      return;
    }

    let form: FormGroupDirective;
    if (type === ServiceRequestStepType.Shipping) {
      form = this.step1Form;
      this.isShippingStepContinuePressed = true;
    } else {
      form = this.step2Form;
      this.isDetailStepContinuePressed = true;
    }

    if (form) form.control.markAllAsTouched();

    if (isAnyControlInvalid(this.formsIds[type], this.el.nativeElement)) {
      return;
    }
    this.stepper.next();
  }

  private setDetailsStepValidators(): void {
    const controlTrackingNumber = this.step2Form.form.get(FormKeys.trackingNumber);
    if (controlTrackingNumber) {
      controlTrackingNumber.setValidators([Validators.maxLength(20)]);
    }

    const poTrackingNumber = this.step2Form.form.get(FormKeys.poNumber);
    if (poTrackingNumber) {
      poTrackingNumber.setValidators([Validators.maxLength(35)]); // 35 is max length for SAP, not in our DB
    }
  }

  closeAndResetStepper() {
    this.stepper.reset();
    this.isStepperVisible = false;
    this.sideServiceRequestStepper.close();
    this.router.navigate([RouteNameConstants.ROOT]);
  }

  downloadFile() {
    const data = this.fileService.base64ToBlob(this.raDocument.blob);
    this.fileService.downloadFile(data, this.raDocument.fileName);
  }

  get shipmentPickupDetails(): ShipmentPickupDetails {
    const dateString = this.step2Form?.control.get(FormKeys.pickupDate)?.value;
    if (!dateString) return undefined;

    return {
      date: new Date(dateString),
      businessHours: `${this.step2Form?.control.get(FormKeys.businessStart)?.value} - ${
        this.step2Form?.control.get(FormKeys.businessEnd)?.value
      }`,
    };
  }

  setVendor(vendor: string) {
    this.ra.update((ra) => ({ ...ra, vendor }));
  }

  ngOnDestroy(): void {
    this._subscriptions$.forEach((x) => x.unsubscribe());
  }

  private getShipmentFormValues(): Shipment {
    const parcelDimensionOptions =
      ParcelDimensionsOptionsByUnitOfMeasure[this.customerSettings.unitOfMeasure];
    const shipment = {
      description: this.step2Form.control.get(FormKeys.parcelsAdditionalInfo).value,
      // Montova does not support dangerous goods, so it is always false
      isDangerousGood: false,
      parcels: (this.step2Form.control.get(FormKeys.shippingParcels).value as ParcelOption[]).map(
        (parcel) => ({
          ...parcelDimensionOptions[parcel.parcelType],
          weight: parcel.weight,
          description: parcel.description?.trim() || undefined,
        }),
      ),
      startDateTime: DateUtils.combineDateAndTimeStrings(
        this.step2Form.control.get(FormKeys.pickupDate).value,
        this.step2Form.control.get(FormKeys.businessStart).value,
      ),
      closeDateTime: DateUtils.combineDateAndTimeStrings(
        this.step2Form.control.get(FormKeys.pickupDate).value,
        this.step2Form.control.get(FormKeys.businessEnd).value,
      ),
    };

    return shipment;
  }
}
